import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BathymetricCanvas } from "./BathymetricCanvas";

export const Section = ({ children, className = "", id }) => (
  <section id={id} className={`py-20 sm:py-28 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

export const Overline = ({ children, light = false }) => (
  <span className={`font-mono text-xs font-semibold uppercase tracking-[0.25em] ${light ? "text-cyan-300" : "text-cyan-700"}`}>
    {children}
  </span>
);

// tone: "light" (on light sections) | "hero" (bright, on dark backgrounds)
export const PrimaryButton = ({ to, children, testId, className = "", tone = "light", onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-2 font-bold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5 ${
      tone === "hero"
        ? "bg-gradient-to-r from-cyan-400 to-teal-400 hover:from-cyan-300 hover:to-teal-300 text-slate-950 shadow-[0_0_24px_rgba(0,240,255,0.35)]"
        : "bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 text-white shadow-lg shadow-cyan-600/25"
    } ${className}`}
  >
    {children}
  </Link>
);

export const SecondaryButton = ({ to, children, testId, className = "", tone = "light", onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-2 font-semibold px-6 py-3.5 rounded-xl border transition-all ${
      tone === "hero"
        ? "bg-white/10 hover:bg-white/15 text-white border-white/25"
        : "bg-white hover:bg-slate-50 text-slate-800 border-slate-300"
    } ${className}`}
  >
    {children}
  </Link>
);

export const Card = ({ children, className = "", testId }) => (
  <div
    data-testid={testId}
    className={`bg-white rounded-2xl border border-slate-200 shadow-[0_4px_20px_rgba(15,23,42,0.04)] hover:shadow-[0_14px_36px_rgba(15,23,42,0.08)] hover:border-cyan-300 transition-all duration-300 ${className}`}
  >
    {children}
  </div>
);

export const IconBadge = ({ icon: Icon, className = "" }) => (
  <div className={`w-12 h-12 rounded-xl bg-cyan-50 border border-cyan-200 flex items-center justify-center ${className}`}>
    <Icon className="w-6 h-6 text-cyan-700" />
  </div>
);

// Reusable bold CTA banner (stays dark as an accent band on light pages)
export const QuoteBanner = () => (
  <Section>
    <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0d1a3a] to-[#0A0F1D] p-10 sm:p-16 shadow-2xl">
      <BathymetricCanvas className="absolute inset-0 w-full h-full opacity-40" />
      <div className="relative max-w-2xl">
        <Overline light>Start Your Survey</Overline>
        <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
          Ready to see what's beneath the surface?
        </h2>
        <p className="mt-4 text-lg text-slate-200 leading-relaxed">
          Tell us about your waterbody and goals. We'll plan the survey, collect the data on-site,
          and deliver finished maps and reports — no software to learn, no fieldwork on your end.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <PrimaryButton to="/request-a-quote" testId="banner-request-quote-button" tone="hero">
            Request a Quote <ArrowRight className="w-4 h-4" />
          </PrimaryButton>
          <SecondaryButton to="/how-it-works" testId="banner-how-it-works-button" tone="hero">
            How It Works
          </SecondaryButton>
        </div>
      </div>
    </div>
  </Section>
);
